'use client';

import RadioCheckboxLabelText, {
  useBgBorder,
} from '@/components/base/form/helpers/radio-checkbox-label-text';
import { clsx } from 'clsx';
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  forwardRef,
} from 'react';
import type { FieldError } from 'react-hook-form';

export interface RadioButtonProps
  extends Omit<ComponentPropsWithoutRef<'input'>, 'required' | 'size'> {
  error?: FieldError;
  label?: ReactNode;
  required?: boolean;
  size?: 'default' | 'small';
  fixedWidth?: boolean;
  testId?: string;
}

const baseClasses = clsx(
  'anim-effect focus-within:focus-outline relative h-m w-m cursor-pointer rounded-full text-default ring-inset hover:outline hover:outline-3 hover:outline-hover hover:ring-1 hover:ring-strong',
);

const inputClasses = clsx('h-m w-m cursor-pointer opacity-0');

const bgClasses = {
  checked: clsx('bg-contrast'),
  default: clsx('bg-main'),
  disabled: clsx('bg-main'),
} as const;

const borderClasses = {
  default: 'ring-1 ring-main',
  error: 'ring-2 ring-error',
};

const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  ({ error, label, fixedWidth, size, required, testId, ...rest }, ref) => {
    const [bgState, borderState] = useBgBorder(
      error,
      rest.disabled,
      rest.checked,
    );

    return (
      <div className={clsx('flex gap-xs', fixedWidth && 'w-full')}>
        <div
          className={clsx(
            baseClasses,
            bgClasses[bgState],
            borderClasses[borderState],
            rest.disabled && 'pointer-events-none',
          )}
        >
          <input
            ref={ref}
            type="checkbox"
            className={clsx(inputClasses)}
            required={!!required}
            data-testid={testId}
            {...rest}
          />
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            aria-hidden
          >
            {rest.checked && (
              <div
                className={clsx(
                  'h-[8px] w-[8px] rounded-full',
                  error ? 'bg-text-error' : 'bg-main',
                )}
              />
            )}
          </div>
        </div>
        <RadioCheckboxLabelText
          label={label}
          isSmall={size === 'small'}
          required={required}
          fixedWidth={fixedWidth}
          id={rest.id}
        />
      </div>
    );
  },
);

RadioButton.displayName = 'RadioButton';

export default RadioButton;
